import React, { useState, useEffect } from 'react';
import { getLocalSiteSettings } from '../lib/siteSettingsService';
import { X } from 'lucide-react';

export const WelcomeFlow: React.FC = () => {
  const [stage, setStage] = useState<'loading' | 'popup' | 'hidden'>('hidden');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // 1. Bypass logic for ALL direct deep links and transactional routes.
    // The Welcome popup should ONLY appear on the true root homepage ('/') for first-time onboarding.
    // It must NOT block transactional flows like invoices, payments, or direct product shares.
    const path = window.location.pathname.toLowerCase();
    const params = new URLSearchParams(window.location.search);
    
    const isDeepLinkQuery = params.has('product') || params.has('p') || params.has('item') || params.has('id');
    const isOrderReturn = params.has('orderSuccess') || params.has('orderFailed');
    
    if ((path !== '/' && path !== '/index.html' && path !== '') || isDeepLinkQuery || isOrderReturn) {
      setStage('hidden');
      return;
    }

    // 2. Determine if we should show the welcome flow
    let hasVisited = false;
    try {
      hasVisited = !!localStorage.getItem('rh_welcome_shown') || !!sessionStorage.getItem('rh_welcome_shown');
    } catch (e) {
      // Fallback: check a memory flag on the window object
      if ((window as any).__rh_welcome_shown) {
        hasVisited = true;
      }
    }
    
    // If user has visited before or refreshed, stay hidden to load page instantly (0ms)
    if (!hasVisited) {
      try {
        localStorage.setItem('rh_welcome_shown', 'true');
        sessionStorage.setItem('rh_welcome_shown', 'true');
        (window as any).__rh_welcome_shown = true;
      } catch (e) {}
      
      setStage('popup');
    }
  }, []);

  const handleClose = () => {
    setStage('hidden');
    try {
      localStorage.setItem('rh_welcome_shown', 'true');
      sessionStorage.setItem('rh_welcome_shown', 'true');
      (window as any).__rh_welcome_shown = true;
    } catch (e) {
      console.warn('storage access note', e);
    }
  };

  const settings = getLocalSiteSettings();
  const logo = settings.siteLogoHeaderUrl || '/assets/logo.svg';

  if (stage === 'hidden') return null;

  const isPopup = stage === 'popup';

  return (
    <div className={`fixed inset-0 z-[99999] flex items-center justify-center px-4 transition-all duration-500 ${
      isPopup ? 'bg-slate-950/95' : 'bg-[#0b101e]'
    }`}>
      {/* LOADING STAGE */}
      {stage === 'loading' && (
        <div className="flex flex-col items-center justify-center animate-in fade-in duration-300 w-full max-w-sm">
          {/* Logo Container */}
          <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-3xl p-[1px] shadow-2xl shadow-indigo-500/10 mb-6">
            <div className="w-full h-full bg-[#111827] rounded-[23px] flex items-center justify-center overflow-hidden p-2">
              <img src={logo} alt="Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          
          <h1 className="text-[22px] sm:text-2xl font-bold text-white tracking-tight mb-1.5 text-center">
            RH Editography
          </h1>
          <p className="text-slate-400 text-[13px] sm:text-[14px] font-medium mb-10 text-center">
            Premium editable design templates
          </p>
          
          {/* Loading Bar */}
          <div className="w-48 sm:w-56 h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* POPUP STAGE */}
      {stage === 'popup' && (
        <div className="w-full max-w-[340px] sm:max-w-[440px] md:max-w-[480px] bg-[#0c1220] border border-slate-800/60 rounded-[24px] p-6 sm:p-8 md:p-10 shadow-2xl relative animate-in zoom-in-[0.98] fade-in duration-300">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 sm:top-5 sm:right-5 text-slate-500 hover:text-white transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
          </button>
          
          <div className="text-center pt-2">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4 flex items-center justify-center gap-2">
              Welcome to RH Editography <span className="text-xl sm:text-2xl md:text-3xl inline-block origin-bottom-right animate-[wave_2s_ease-in-out_infinite]">👋</span>
            </h2>
            <p className="text-slate-400 text-[14px] sm:text-[15px] md:text-[16px] leading-relaxed mb-7 md:mb-9 px-1 sm:px-4">
              Explore our premium collection of digital templates and assets designed for professionals.
            </p>
            
            <button 
              onClick={handleClose}
              className="w-full py-3.5 md:py-4 rounded-2xl bg-[#8b7dff] hover:bg-[#7b6dff] text-white font-semibold text-[15px] md:text-[16px] shadow-lg shadow-[#8b7dff]/20 transition-all"
            >
              Browse templates
            </button>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes wave {
          0% { transform: rotate(0.0deg) }
          10% { transform: rotate(14.0deg) }
          20% { transform: rotate(-8.0deg) }
          30% { transform: rotate(14.0deg) }
          40% { transform: rotate(-4.0deg) }
          50% { transform: rotate(10.0deg) }
          60% { transform: rotate(0.0deg) }
          100% { transform: rotate(0.0deg) }
        }
      `}} />
    </div>
  );
};
